import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Shield, 
  Send, 
  ChevronDown, 
  MapPin, 
  Phone, 
  Mail, 
  CheckCircle2 
} from 'lucide-react';

const ContactSection = () => {
  const [activeTab, setActiveTab] = useState('contact');
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    companyName: '', 
    serviceType: '',
    message: ''
  });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [expandedFAQ, setExpandedFAQ] = useState(null);
  const [errors, setErrors] = useState({});
  const [submissionSuccess, setSubmissionSuccess] = useState(false); // New state for success message


  const serviceTypes = [
    'Technical Support',             
    'Account Issues',               
    'Billing Inquiries',          
    'Service Feedback',           
    'Feature Requests',            
    'Reporting Abuse',               
    'Security Concerns',           
    'Compliance Inquiries',       
    'General Questions',          
    'Business Partnerships',      
    'Consultation Services',        
    'Logout Issues',                
    'Content Inquiries',             
    'Feedback on User Experience',
    'Threat Intelligence',
    'Incident Response',
    'Penetration Testing',
    'Security Consulting',
    'Managed Security Services',
    'Compliance',
    'Vulnerability Assessment',
    'Security Awareness Training',
    'Risk Assessment',
    'Malware Analysis',
    'Forensics',
    'Data Protection',
    'Audit Services',
    'Network Security',
    'Cloud Security',
    'Disaster Recovery Planning',    
];

  const faqs = [
    {
      category: 'Services',
      items: [
        {
          question: "What is 0X10LayerFF?",
          answer: "We are a team of cybersecurity professionals providing an all-in-one security platform."
        },
        {
          question: "Is 0x10layerff free to use?",
          answer: "Some of our tools are free to use, while others may require a subscription or one-time payment. Please see our pricing page for more details."
        },
        {
          question: "How do I use the tools on your website?",
          answer: "Simply navigate to the specific tool you wish to use, follow the instructions on the page, and input your data as required. Each tool has user-friendly prompts to guide you through the process."
        }
      ]
    },
    {
      category: 'Technical',
      items: [
        {
          question: "Can I report security vulnerabilities?",
          answer: " Yes! Please contact us at support@layerff.com"
        },
        {
          question: "What kind of support do you offer?",
          answer: "We offer email support and a comprehensive knowledge base."
        }
      ]
    }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    let isValid = true;
    const newErrors = {};

    if (!formData.fullName.trim()) {
        newErrors.fullName = 'Full Name is required';
        isValid = false;
    }

    if (!formData.email.trim()) {
        newErrors.email = 'Business Email is required';
        isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = 'Invalid email format';
        isValid = false;
    }

    if (!formData.serviceType) {
        newErrors.serviceType = 'Service Type is required';
        isValid = false;
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
      isValid = false;
    } else if (formData.message.trim().length < 15) { 
        newErrors.message = 'Message is to short';
        isValid = false;
    } else if (formData.message.trim().length > 10000) { 
        newErrors.message = 'Message is to long';
        isValid = false;
    }

    setErrors(newErrors);
    setTimeout(() => {
      setErrors({});    
    }, 5000);
    return isValid;
};

const handleSubmit = async (e) => {
    e.preventDefault();

    if (validateForm()) {
        try {
            const response = await fetch('/uss/contactUs', { 
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData),
            });

            if (response.ok) {
                setFormData({
                    fullName: '',
                    email: '',
                    companyName: '',
                    serviceType: '',
                    message: '',
                });
                setFormSubmitted(true);
                setSubmissionSuccess(true); 
                setTimeout(() => setSubmissionSuccess(false), 5000)

            } else {
                const data = await response.json();
                setErrors({ submit: data.error || 'Failed to submit form' });
                setTimeout(() => {
                  setErrors({});    
                }, 5000)
            }
        } catch (err) {
            setErrors({ submit: 'An error occurred while submitting the form' });
            setTimeout(() => {
              setErrors({});    
            }, 5000)
        }
    }
};

  const renderContactForm = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className={`bg-gray-900/60 backdrop-blur-lg border rounded-2xl p-8 shadow-2xl 
        ${submissionSuccess ? 'border-green-400' : errors.submit || Object.keys(errors).length > 0 ? 'border-red-500' : 'border-gray-700/50'}`}
  >
      {submissionSuccess && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative" role="alert">
              <strong className="font-bold">Success!</strong>
              <span className="block sm:inline"> Your message has been sent.</span>
          </div>
      )}

      {!formSubmitted ? (
          <form onSubmit={handleSubmit} className="space-y-6" autoComplete="off"> {/* Disable autocomplete at the form level */}
              {/* Form fields with error handling */}
              <div className="grid md:grid-cols-2 gap-6">
                  <div>
                      <label className="block text-gray-300 mb-2">Full Name</label>
                      <input
                          type="text"
                          name="fullName"
                          value={formData.fullName}
                          onChange={handleInputChange}
                          required
                          autoComplete="off"
                          className={`w-full px-4 py-3 bg-gray-800 border ${errors.fullName ? 'border-red-500' : 'border-gray-700'} rounded-xl focus:ring-2 focus:ring-blue-500 text-white`}
                      />
                      {errors.fullName && <p className="text-red-500 text-sm mt-1 text-center">{errors.fullName}</p>}
                  </div>
                  <div>
                      <label className="block text-gray-300 mb-2">Email</label>
                      <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          autoComplete="off"
                          className={`w-full px-4 py-3 bg-gray-800 border ${errors.email ? 'border-red-500' : 'border-gray-700'} rounded-xl focus:ring-2 focus:ring-blue-500 text-white`}
                      />
                      {errors.email && <p className="text-red-500 text-sm mt-1 text-center">{errors.email}</p>}
                  </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                  <div>
                      <label className="block text-gray-300 mb-2">Company Name</label>
                      <input
                          type="text"
                          name="companyName"
                          value={formData.companyName}
                          onChange={handleInputChange}
                          autoComplete="off"
                          className={`w-full px-4 py-3 bg-gray-800 border ${errors.companyName ? 'border-red-500' : 'border-gray-700'} rounded-xl focus:ring-2 focus:ring-blue-500 text-white`}
                      />
                      {errors.companyName && <p className="text-red-500 text-sm mt-1 text-center">{errors.companyName}</p>}
                  </div>
                  <div>
                      <label className="block text-gray-300 mb-2">Service Type</label>
                      <select
                          name="serviceType"
                          value={formData.serviceType}
                          onChange={handleInputChange}
                          required
                          className={`w-full px-4 py-3 bg-gray-800 border ${errors.serviceType ? 'border-red-500' : 'border-gray-700'} rounded-xl focus:ring-2 focus:ring-blue-500 text-white`}
                      >
                          <option value="">Select Service</option>
                          {serviceTypes.map(type => (
                              <option key={type} value={type}>{type}</option>
                          ))}
                      </select>
                      {errors.serviceType && <p className="text-red-500 text-sm mt-1 text-center">{errors.serviceType}</p>}
                  </div>
              </div>

              <div>
                  <label className="block text-gray-300 mb-2">Your Message</label>
                  <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows="5"
                      className={`w-full px-4 py-3 bg-gray-800 border ${errors.message ? 'border-red-500' : 'border-gray-700'} rounded-xl focus:ring-2 focus:ring-blue-500 text-white`}
                      placeholder="Describe your cybersecurity requirements in detail"
                  />
                  {errors.message && <p className="text-red-500 text-sm mt-1 text-center">{errors.message}</p>}
              </div>

              <button
                  type="submit"
                  className="w-full flex items-center justify-center gap-3 bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 rounded-xl transition-all duration-300 transform hover:scale-[1.02]"
              >
                  <Send className="w-5 h-5" />
                  Submit Inquiry
              </button>
              {errors.submit && <p className='text-red-500 text-center text-sm mt-2'>{errors.submit}</p>}
          </form>
      ) : (
          <div className="text-center py-12">
              <CheckCircle2 className="mx-auto w-24 h-24 text-green-500 mb-6" />
              <h3 className="text-2xl font-bold text-white mb-4">Inquiry Received</h3>
              <p className="text-gray-400">Our team will review your request and contact you if neccessary.</p>
          </div>
      )}
  </motion.div>
  );

  const renderFAQSection = () => (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="space-y-6"
    >
      {faqs.map((category, catIndex) => (
        <div key={catIndex} className="bg-gray-900/60 backdrop-blur-lg border border-gray-700/50 rounded-2xl p-6">
          <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
            <Shield className="w-6 h-6 mr-3 text-blue-500" />
            {category.category} FAQs
          </h3>
          {category.items.map((faq, faqIndex) => (
            <div 
              key={faqIndex} 
              className="border-t border-gray-700 py-4"
            >
              <button
                onClick={() => setExpandedFAQ(
                  expandedFAQ === `${catIndex}-${faqIndex}` 
                    ? null 
                    : `${catIndex}-${faqIndex}`
                )}
                className="w-full flex justify-between items-center text-left"
              >
                <span className="text-white font-medium">{faq.question}</span>
                <ChevronDown 
                  className={`w-5 h-5 text-blue-500 transition-transform ${
                    expandedFAQ === `${catIndex}-${faqIndex}` ? 'rotate-180' : ''
                  }`} 
                />
              </button>
              <AnimatePresence>
                {expandedFAQ === `${catIndex}-${faqIndex}` && (
                  <motion.p
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="text-gray-400 mt-4"
                  >
                    {faq.answer}
                  </motion.p>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      ))}
    </motion.div>
  );

  return (
    <section className="bg-gray-950 min-h-screen flex items-center justify-center py-16 px-4">
      <div className="max-w-6xl w-full grid md:grid-cols-2 gap-12">
        {/* Sidebar Navigation */}
        <div className="space-y-6">
          <div className="bg-gray-900/60 backdrop-blur-lg border border-gray-700/50 rounded-2xl p-2 flex">
            {['contact', 'faq'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`w-full py-3 rounded-xl transition-all duration-300 ${
                  activeTab === tab 
                    ? 'bg-blue-600 text-white' 
                    : 'text-gray-400 hover:bg-gray-800'
                }`}
              >
                {tab === 'contact' ? 'Contact Us' : 'FAQ'}
              </button>
            ))}
          </div>

          <div className="bg-gray-900/60 backdrop-blur-lg border border-gray-700/50 rounded-2xl p-6 space-y-6">
            <h2 className="text-xl text-white font-bold">Contact Us</h2>
            <p className="text-gray-300">
              At LayerFF, we are dedicated to providing top-notch cybersecurity solutions. Reach out to us through the following contact methods:
            </p>
            <div className="flex items-center space-x-4">
              <Mail className="w-6 h-6 text-blue-500" />
              <span className="text-white">support@layerff.com</span>
            </div>
            <div className="flex items-center space-x-4">
              <Phone className="w-6 h-6 text-blue-500" />
              <span className="text-white">+256 774708048</span>
            </div>
            <div className="flex items-center space-x-4">
              <Phone className="w-6 h-6 text-blue-500" />
              <span className="text-white">+256 772886750</span>
            </div>
            <div className="flex items-center space-x-4">
              <MapPin className="w-6 h-6 text-blue-500" />
              <span className="text-white">Global Cyber Defense HQ</span>
            </div>
            
           
          </div>
        </div>

        {/* Dynamic Content Area */}
        <div>
          {activeTab === 'contact' ? renderContactForm() : renderFAQSection()}
        </div>
      </div>
    </section>
  );
};

export default ContactSection;